/**
 * Điều khoản sử dụng + Chính sách quyền riêng tư của SAPA — MỘT nguồn duy nhất (30/09/2026).
 *
 * Dùng ở hai nơi từ cùng nội dung này, để không bao giờ lệch nhau:
 *   1. Màn đọc trong Mini App khách (`apps/miniapp/src/phap-ly.tsx`).
 *   2. Hai trang web công khai `sapa.autopostvn.com/phap-ly/*.html` (dán link vào Mini App Center) — sinh bằng
 *      `pnpm phap-ly:tao`, test `phap-ly.test.ts` báo đỏ nếu file trang web cũ hơn nguồn này.
 *
 * Chỉ ghi những gì SAPA thật sự làm (đối chiếu mã 30/09): Zalo ID + tên hiển thị, số điện thoại chỉ khi khách cho
 * phép, lịch hẹn, gói/ví/điểm do tiệm ghi, ảnh chỉ khi khách đồng ý tại quầy và rút được trong "Của tôi", nhật ký kỹ
 * thuật. Không import `node:*` — file này chạy cả trong trình duyệt và Mini App (`pnpm check:web-safe`).
 */

export const LIEN_HE = {
  congTy: 'Công ty TNHH AutoPost VN',
  maSoThue: '0319267826',
  dienThoai: [
    { so: '0987 939 605', ghiChu: '' },
    { so: '0973 158 895', ghiChu: 'Mr. Cường' },
  ],
  email: 'support@autopostvn.com',
  diaChi: 'F3/31, lô 2, ấp 6, Xã Bình Lợi, TP. Hồ Chí Minh',
} as const

/** Số nhận hỗ trợ và khiếu nại về ứng dụng — hiện ở chân pháp lý Mini App (Thoả thuận Zalo 5.5.a). */
export const SDT_HO_TRO = LIEN_HE.dienThoai[0].so

/** Ngày cập nhật, ghi ở đầu cả hai văn bản. */
export const NGAY_CAP_NHAT_PHAP_LY = '31/10/2026'

export interface MucPhapLy {
  readonly tieuDe: string
  /** Các đoạn văn. */
  readonly doan?: readonly string[]
  /** Danh sách gạch đầu dòng, hiện sau các đoạn văn. */
  readonly y?: readonly string[]
}

export interface VanBanPhapLy {
  readonly ma: 'dieu-khoan' | 'quyen-rieng-tu' | 'dieu-khoan-cua-hang'
  readonly tieuDe: string
  readonly gioiThieu: string
  readonly muc: readonly MucPhapLy[]
}

const DT = LIEN_HE.dienThoai
  .map((d) => (d.ghiChu === '' ? d.so : `${d.so} (${d.ghiChu})`))
  .join(' · ')
const CACH_LIEN_HE = `Địa chỉ: ${LIEN_HE.diaChi}. Điện thoại: ${DT}. Email: ${LIEN_HE.email}.`

export const DIEU_KHOAN: VanBanPhapLy = {
  ma: 'dieu-khoan',
  tieuDe: 'Điều khoản sử dụng',
  gioiThieu:
    'SAPA là ứng dụng đặt lịch hẹn trên Zalo cho các cửa hàng dịch vụ. ' +
    'Khi bạn dùng SAPA để xem dịch vụ, chọn giờ, đặt, đổi hoặc huỷ lịch, bạn đồng ý với các điều khoản dưới đây.',
  muc: [
    {
      tieuDe: '1. Đơn vị vận hành',
      doan: [
        `SAPA do ${LIEN_HE.congTy} (mã số thuế ${LIEN_HE.maSoThue}) vận hành. SAPA cung cấp công cụ đặt lịch và quản lý lịch hẹn cho cửa hàng.`,
      ],
    },
    {
      tieuDe: '2. Vai trò của SAPA và của cửa hàng',
      doan: [
        'Dịch vụ bạn đặt do cửa hàng trực tiếp cung cấp. SAPA không bán dịch vụ và không quyết định chất lượng, giá hay thời gian thực hiện.',
        'Giá, thời lượng, tiền cọc và chính sách đổi – huỷ lịch do cửa hàng quyết định và được hiển thị cho bạn trước khi bạn xác nhận.',
      ],
    },
    {
      tieuDe: '3. Đặt lịch',
      y: [
        'Bạn cung cấp thông tin đúng và chỉ đặt lịch khi bạn thật sự có nhu cầu.',
        'Khi bạn chọn giờ, hệ thống giữ chỗ tạm thời trong một thời gian ngắn. Lịch chỉ được coi là đặt xong khi bạn bấm xác nhận.',
        'Một số cửa hàng cần tự xác nhận lịch hoặc yêu cầu đặt cọc. Trong trường hợp đó, lịch có hiệu lực khi cửa hàng xác nhận.',
        'Cửa hàng có thể đổi giờ hoặc huỷ lịch khi có lý do chính đáng; bạn sẽ được báo trong mục “Của tôi” hoặc qua Zalo.',
        'Bạn tự đổi hoặc huỷ lịch trong mục “Của tôi”, trong thời hạn cửa hàng cho phép.',
      ],
    },
    {
      tieuDe: '4. Thanh toán, tiền cọc, gói và ví',
      doan: [
        'SAPA không thu hộ và không giữ tiền của bạn. Bạn thanh toán trực tiếp cho cửa hàng.',
        'Nếu cửa hàng yêu cầu đặt cọc, bạn chuyển khoản trực tiếp vào tài khoản của cửa hàng theo hướng dẫn trên màn hình. Việc trừ cọc vào tiền dịch vụ hoặc hoàn cọc thực hiện theo chính sách của cửa hàng.',
        'Gói dịch vụ, ví trả trước và điểm thưởng do cửa hàng phát hành và quản lý. SAPA chỉ hiển thị số dư và lịch sử để bạn theo dõi.',
      ],
    },
    {
      tieuDe: '5. Tin nhắn từ SAPA và cửa hàng',
      doan: [
        'Bạn có thể nhận tin qua Zalo khi cửa hàng bật tính năng nhắc lịch, như nhắc lịch, biên nhận thanh toán hoặc báo gói sắp hết hạn. Cửa hàng chưa bật thì có thể liên hệ bạn bằng cách khác, như gọi điện hoặc nhắn tay. Các tin này chỉ phục vụ chính lịch hẹn của bạn.',
        'Tin ưu đãi của cửa hàng chỉ gửi khi bạn đồng ý. Bạn tắt bất cứ lúc nào trong mục “Của tôi”.',
      ],
    },
    {
      tieuDe: '6. Trách nhiệm của người dùng',
      y: [
        'Không đặt lịch giả, không đặt lịch hàng loạt hoặc lạm dụng việc giữ chỗ làm ảnh hưởng người khác.',
        'Không dùng thông tin của người khác khi chưa được đồng ý.',
        'Không tìm cách truy cập trái phép, làm gián đoạn hoặc gây hại cho hệ thống.',
      ],
    },
    {
      tieuDe: '7. Giới hạn trách nhiệm',
      doan: [
        'Chúng tôi cố gắng để SAPA hoạt động ổn định nhưng không thể bảo đảm không bao giờ gián đoạn. Khi có sự cố, bạn nên liên hệ trực tiếp với cửa hàng để được sắp xếp.',
        'Trong phạm vi pháp luật cho phép, SAPA không chịu trách nhiệm về chất lượng dịch vụ do cửa hàng cung cấp hay thoả thuận riêng giữa bạn và cửa hàng.',
        'SAPA là phần mềm đặt lịch hẹn do Công ty TNHH AutoPost VN cung cấp cho các cửa hàng. Mỗi cửa hàng tự chịu trách nhiệm về giấy phép, điều kiện kinh doanh ngành nghề và dịch vụ mình cung cấp.',
      ],
    },
    {
      tieuDe: '8. Dữ liệu cá nhân',
      doan: [
        'Cách chúng tôi thu thập và bảo vệ thông tin của bạn được nêu trong Chính sách quyền riêng tư.',
      ],
    },
    {
      tieuDe: '9. Thay đổi điều khoản và luật áp dụng',
      doan: [
        'Chúng tôi có thể cập nhật điều khoản này; bản mới được đăng công khai kèm ngày cập nhật. Việc bạn tiếp tục sử dụng SAPA sau đó nghĩa là bạn đồng ý với bản mới.',
        'Điều khoản này tuân theo pháp luật Việt Nam.',
      ],
    },
    {
      tieuDe: '10. Liên hệ, hỗ trợ và khiếu nại',
      doan: [
        `${LIEN_HE.congTy} — MST ${LIEN_HE.maSoThue}.`,
        CACH_LIEN_HE,
        'Góp ý hoặc khiếu nại về ứng dụng: gọi điện thoại hoặc gửi email ở trên, nêu tên cửa hàng và mã đặt lịch (nếu có). Chúng tôi phản hồi trong 3 ngày làm việc.',
        'Khiếu nại về dịch vụ của cửa hàng: bạn liên hệ thẳng với cửa hàng (nút Gọi hoặc Nhắn trong ứng dụng). Nếu chưa được giải quyết, bạn báo cho SAPA để chúng tôi hỗ trợ kết nối.',
      ],
    },
  ],
}

export const QUYEN_RIENG_TU: VanBanPhapLy = {
  ma: 'quyen-rieng-tu',
  tieuDe: 'Chính sách quyền riêng tư',
  gioiThieu:
    'Chính sách này giải thích SAPA thu thập những thông tin nào khi bạn đặt lịch hẹn, dùng để làm gì, chia sẻ với ai và bạn có những quyền gì. ' +
    'Chúng tôi xử lý dữ liệu cá nhân theo Nghị định 13/2023/NĐ-CP về bảo vệ dữ liệu cá nhân.',
  muc: [
    {
      tieuDe: '1. Ai chịu trách nhiệm về dữ liệu của bạn',
      doan: [
        `${LIEN_HE.congTy} (MST ${LIEN_HE.maSoThue}) vận hành SAPA và xử lý dữ liệu thay mặt các cửa hàng.`,
        'Cửa hàng bạn đặt lịch là nơi quyết định vì sao cần dữ liệu của bạn và dùng cho việc gì. Mỗi cửa hàng chỉ xem được khách của chính mình.',
      ],
    },
    {
      tieuDe: '2. Thông tin chúng tôi thu thập',
      y: [
        'Mã định danh Zalo (ID): khi bạn mở Mini App và đặt lịch, để nhận ra bạn ở lần sau và ghép đúng hồ sơ khách của cửa hàng. Tên hiển thị: chỉ khi bạn bấm cho phép. Ảnh đại diện Zalo của bạn không được thu thập.',
        'Số điện thoại: chỉ khi bạn bấm cho phép. Zalo có thể hỏi tên và số điện thoại cùng một lần, ở bước xác nhận đặt lịch; bạn từ chối vẫn đặt lịch được. Nếu máy ghi nhớ được lựa chọn của bạn thì chúng tôi không hỏi lại ở mỗi lần đặt. Bạn có thể cho phép sau bằng nút "Dùng số Zalo để cửa hàng nhận ra bạn" (hiện ở mục “Của tôi” khi cửa hàng chưa nhận ra bạn), hoặc khi nhận ưu đãi.',
        'Thông tin lịch hẹn: dịch vụ, ngày giờ, chi nhánh, nhân viên, mã lịch, trạng thái và lời nhắn bạn gửi cho cửa hàng.',
        'Thông tin do cửa hàng ghi cho bạn: gói dịch vụ, ví trả trước, điểm thưởng, lịch sử sử dụng và hoá đơn.',
        'Ảnh: chỉ khi bạn đồng ý tại quầy của cửa hàng. Bạn rút đồng ý được trong mục “Của tôi”, ảnh đã lưu sẽ bị xoá.',
        'Góp ý bạn tự gửi trong ứng dụng: nội dung bạn viết (số điện thoại nếu có sẽ tự bị che), loại góp ý, màn đang mở, phiên bản ứng dụng, khổ màn hình, cửa hàng bạn đang xem và thời điểm gửi. Góp ý không kèm tên, số điện thoại hay tài khoản Zalo của bạn.',
        'Thông tin kỹ thuật tối thiểu như địa chỉ IP và thời điểm truy cập, để bảo mật và xử lý sự cố.',
      ],
    },
    {
      tieuDe: '3. Chúng tôi dùng thông tin để làm gì',
      y: [
        'Đặt, xác nhận, đổi và huỷ lịch hẹn.',
        'Gửi tin qua Zalo: xác nhận lịch, nhắc lịch, biên nhận thanh toán, báo gói sắp hết hạn.',
        'Giúp cửa hàng nhận ra khách quen và phục vụ đúng lịch sử của bạn.',
        'Hỗ trợ khi bạn hoặc cửa hàng cần liên hệ; bảo mật hệ thống và ngăn chặn gian lận.',
        'Góp ý bạn tự gửi trong ứng dụng: chỉ đội ngũ SAPA đọc để sửa chỗ khó dùng của ứng dụng; không chuyển cho cửa hàng, không trả lời riêng, và tự xoá sau 180 ngày.',
        'Tuân thủ nghĩa vụ theo quy định pháp luật.',
      ],
      doan: [
        'Tên và số điện thoại của bạn chỉ dùng để cửa hàng nhận ra bạn, ghép đúng hồ sơ khách, gọi khi cần đổi giờ và gửi tin liên quan đến lịch hẹn khi cửa hàng bật tính năng nhắc lịch. Chúng tôi không bán dữ liệu của bạn và không dùng để quảng cáo cho bên thứ ba.',
      ],
    },
    {
      tieuDe: '4. Chia sẻ thông tin với ai',
      y: [
        'Cửa hàng mà bạn đặt lịch: để cửa hàng phục vụ bạn.',
        'Zalo: để gửi tin nhắn và xác thực tài khoản Zalo của bạn.',
        'Nhà cung cấp hạ tầng máy chủ, chỉ để vận hành hệ thống và có nghĩa vụ bảo mật. Riêng ảnh bạn đồng ý để cửa hàng lưu có thể được lưu trên máy chủ đám mây của nhà cung cấp đặt ngoài Việt Nam (hiện là Cloudflare); khi kho đám mây gặp sự cố, ảnh được lưu tạm trên máy chủ của SAPA.',
        'Cơ quan nhà nước có thẩm quyền, khi pháp luật yêu cầu.',
      ],
      doan: ['Ngoài các trường hợp trên, chúng tôi không chia sẻ dữ liệu của bạn cho ai khác.'],
    },
    {
      tieuDe: '5. Lưu trữ và bảo mật',
      y: [
        'Dữ liệu truyền qua kết nối mã hoá HTTPS.',
        'Dữ liệu của mỗi cửa hàng được tách riêng; nhân viên chỉ xem được phần việc được phân quyền.',
        'Mã đăng nhập và khoá bí mật chỉ nằm ở máy chủ, không nằm trong ứng dụng của bạn.',
        'Ảnh bạn đồng ý lưu tự xoá sau thời hạn đã nêu khi bạn đồng ý (hiện tối đa 70 ngày), hoặc khi bạn rút đồng ý: ảnh ngừng hiển thị ngay và được xoá khỏi máy chủ trong vòng 1 ngày.',
      ],
      doan: [
        'Chúng tôi chỉ giữ dữ liệu trong thời gian cần thiết để phục vụ lịch hẹn và theo quy định pháp luật. Riêng số liệu tài chính (hoá đơn, sổ ghi thanh toán) phải giữ theo quy định về kế toán.',
      ],
    },
    {
      tieuDe: '6. Quyền của bạn',
      doan: [
        'Bạn có quyền được biết, đồng ý hoặc rút lại sự đồng ý, xem, chỉnh sửa, yêu cầu xoá hoặc hạn chế xử lý, và phản đối việc xử lý dữ liệu cá nhân của mình.',
      ],
      y: [
        'Tự làm ngay trong mục “Của tôi”: tắt nhận tin ưu đãi; rút đồng ý lưu ảnh.',
        'Rút quyền sử dụng dữ liệu trên Zalo: khi bạn làm việc này trong phần quản lý quyền của Zalo, Zalo báo cho SAPA và hồ sơ của bạn tại các cửa hàng được ẩn danh (xoá tên, số điện thoại, mã Zalo; ảnh và ghi chú bị xoá; lịch sắp tới bị huỷ). Số liệu tài chính đã ghi vẫn được giữ theo luật kế toán nhưng không còn gắn với bạn.',
        'Các yêu cầu khác (xem, sửa, xoá dữ liệu): nói với cửa hàng hoặc liên hệ SAPA theo mục 8. Chúng tôi phản hồi trong thời hạn Nghị định 13/2023/NĐ-CP quy định.',
      ],
    },
    {
      tieuDe: '7. Trẻ em',
      doan: [
        'SAPA không dành cho trẻ dưới 16 tuổi tự đặt lịch. Người đại diện hợp pháp đặt lịch thay và chịu trách nhiệm về thông tin đã cung cấp.',
      ],
    },
    {
      tieuDe: '8. Liên hệ và thay đổi chính sách',
      doan: [
        `${LIEN_HE.congTy} — MST ${LIEN_HE.maSoThue}.`,
        CACH_LIEN_HE,
        'Khi chính sách thay đổi, bản mới được đăng công khai kèm ngày cập nhật.',
      ],
    },
  ],
}

export const VAN_BAN_PHAP_LY: readonly VanBanPhapLy[] = [DIEU_KHOAN, QUYEN_RIENG_TU]

/*
 * ------------------------------------------------------------------ Điều khoản dịch vụ cho cửa hàng (5.4)
 * Kế hoạch 10/2026, hạng mục 5.4 + ADR-094. Đây là thoả thuận giữa SAPA và CỬA HÀNG (không phải khách), nên KHÔNG nằm
 * trong `VAN_BAN_PHAP_LY` (hai trang công khai dán vào Mini App Center) và KHÔNG sinh trang tĩnh: máy chủ chỉ phát
 * trang này khi cờ `DIEU_KHOAN_CUA_HANG_BAT=1` (route `routes/dieu-khoan-cua-hang.ts`), cờ tắt thì 404 — văn bản chưa
 * được duyệt không bao giờ lên mạng.
 *
 * NỘI DUNG DƯỚI ĐÂY LÀ BẢN NHÁP do SAPA soạn để chủ dự án / luật sư rà. Khi được duyệt: sửa chữ cho đúng bản duyệt,
 * đặt `DIEU_KHOAN_CUA_HANG_BAN_NHAP = false`, ĐỔI `DIEU_KHOAN_CUA_HANG_PHIEN_BAN` (mọi tiệm sẽ thấy dải "SAPA cập nhật
 * điều khoản" và phải bấm Đồng ý lại), chạy `pnpm phap-ly:tao`. Chỗ ghi `[CẦN QUYẾT: …]` là chỗ chưa ai quyết.
 */

/** Phiên bản văn bản (ngày soạn, `YYYY-MM-DD`). Ghi vào `audit_log` lúc mở tiệm / lúc bấm Đồng ý. */
export const DIEU_KHOAN_CUA_HANG_PHIEN_BAN = '2026-10-30'
/** Ngày hiện trên đầu trang (dạng đọc được của phiên bản). */
export const DIEU_KHOAN_CUA_HANG_NGAY = '30/10/2026'
/** `true` = chưa luật sư rà: trang và tệp nháp mang dải "BẢN NHÁP". Đặt `false` chỉ khi chủ dự án duyệt văn bản. */
// Chủ dự án duyệt 30/10/2026 (quyết 5 điểm: báo trước 3 ngày, không hoàn phí, giữ 70 ngày, báo sự cố 6 giờ, trần 3 lần giá gói).
export const DIEU_KHOAN_CUA_HANG_BAN_NHAP = false
/** Câu máy chủ trả khi thiếu ô đồng ý; máy khách nhận ra câu này để hiện ô tích dù chưa biết cờ. */
export const CAU_CAN_DONG_Y_DIEU_KHOAN =
  'Bạn đọc và tích đồng ý Điều khoản dịch vụ để mở cửa hàng nhé.'
/** Đường dẫn máy chủ phát văn bản (chỉ khi cờ bật). */
export const DUONG_DAN_DIEU_KHOAN_CUA_HANG = '/v1/public/phap-ly/dieu-khoan-cua-hang'

export const DIEU_KHOAN_CUA_HANG: VanBanPhapLy = {
  ma: 'dieu-khoan-cua-hang',
  tieuDe: 'Điều khoản dịch vụ cho cửa hàng',
  gioiThieu:
    'Văn bản này là thoả thuận giữa SAPA và cửa hàng dùng SAPA. Cửa hàng tích “đồng ý” khi mở cửa hàng trên SAPA, hoặc khi ' +
    'bấm “Đồng ý” ở dải thông báo khi SAPA cập nhật điều khoản. Dữ liệu khách là của cửa hàng; SAPA giữ và xử lý hộ.',
  muc: [
    {
      tieuDe: '1. Bên cung cấp và dịch vụ',
      doan: [
        `SAPA do ${LIEN_HE.congTy} (mã số thuế ${LIEN_HE.maSoThue}) vận hành. SAPA cung cấp công cụ đặt lịch hẹn và quản lý cửa hàng: bảng lịch ở quầy, ứng dụng đặt lịch cho khách trên Zalo, sổ khách, gói, ví trả trước và báo cáo.`,
        'Người đồng ý điều khoản này là chủ cửa hàng, hoặc người được chủ cửa hàng uỷ quyền và chịu trách nhiệm về việc đó.',
      ],
    },
    {
      tieuDe: '2. Vai trò về dữ liệu cá nhân của khách',
      doan: [
        'Theo Luật Bảo vệ dữ liệu cá nhân và Nghị định 13/2023/NĐ-CP: cửa hàng là bên quyết định vì sao và dùng dữ liệu khách vào việc gì (bên kiểm soát dữ liệu). SAPA là bên xử lý dữ liệu: chỉ lưu, hiển thị và xử lý dữ liệu đó để cung cấp dịch vụ cho cửa hàng, theo chỉ dẫn của cửa hàng.',
        'SAPA không bán dữ liệu khách của cửa hàng, không đưa dữ liệu của cửa hàng này cho cửa hàng khác, và không dùng dữ liệu đó để quảng cáo cho mình hay cho bên thứ ba.',
        'Cách SAPA thu thập và bảo vệ dữ liệu của khách khi họ đặt lịch được nêu trong Chính sách quyền riêng tư công khai của SAPA.',
      ],
    },
    {
      tieuDe: '3. Nghĩa vụ của cửa hàng',
      y: [
        'Chỉ nhập vào SAPA khách của chính cửa hàng mình, và chỉ nhập khi có cơ sở hợp pháp (khách đã cung cấp thông tin hoặc đồng ý cho cửa hàng lưu).',
        'Không nhập lại hồ sơ của người đã yêu cầu xoá dữ liệu hoặc đã rút đồng ý; không đưa vào SAPA dữ liệu của người không phải khách của cửa hàng.',
        'Giữ bí mật mã đăng nhập, chỉ cấp quyền cho đúng người cần, và thu hồi quyền của nhân viên đã nghỉ.',
        'Ghi giá, dịch vụ, chính sách đổi – huỷ đúng sự thật. Tự chịu trách nhiệm về giấy phép và điều kiện kinh doanh của ngành mình.',
        'Không dùng SAPA để gửi tin rác, đặt lịch giả hoặc gây hại cho khách và cho hệ thống.',
        'Chỉ tải lên ảnh bìa và logo mà cửa hàng có quyền dùng, và người xuất hiện trong ảnh đã đồng ý; cửa hàng đồng ý để SAPA hiển thị ảnh đó cho khách trên ứng dụng đặt lịch, và SAPA được gỡ ảnh khi có khiếu nại hoặc vi phạm. Sau khi ảnh được gỡ, ảnh có thể còn hiện tạm thời ở máy của khách cho tới khi khách mở lại trang cửa hàng.',
      ],
    },
    {
      tieuDe: '4. Yêu cầu của khách về dữ liệu của họ',
      doan: [
        'Khi khách muốn xem, sửa hoặc xoá dữ liệu, cửa hàng là nơi tiếp nhận và trả lời; SAPA hỗ trợ cửa hàng thực hiện trong hệ thống.',
        'Khi khách rút quyền dùng dữ liệu trên Zalo, SAPA tự ẩn danh hồ sơ của họ ở mọi cửa hàng dùng SAPA. Số liệu tài chính (hoá đơn, sổ ví, sổ gói) vẫn được giữ theo quy định kế toán, nhưng không còn gắn với tên hay số điện thoại.',
      ],
    },
    {
      tieuDe: '5. Phí dịch vụ',
      doan: [
        'Hiện SAPA miễn phí cho cửa hàng. Khi bắt đầu thu phí, SAPA báo trước ít nhất 3 ngày, mỗi ngày nhắc một lần vào một giờ cố định trong ngày, và cửa hàng chọn dùng tiếp hay không; SAPA không tự trừ tiền.',
        'Tin nhắn Zalo tự động tính theo đúng giá vốn của nhà cung cấp, cửa hàng nạp trước khi dùng.',
        'Phí đã thanh toán không được hoàn lại khi cửa hàng ngừng dùng SAPA giữa kỳ.',
      ],
    },
    {
      tieuDe: '6. Hết hạn, tạm ngưng và ngừng dùng',
      doan: [
        'Khi cửa hàng tạm ngưng, SAPA có thể dừng phần dành cho khách (đặt lịch trên Zalo, tin nhắn tự động). Dữ liệu của cửa hàng vẫn được giữ 70 ngày kể từ ngày tạm ngưng để cửa hàng dùng lại hoặc tải về; sau thời hạn này SAPA có thể xoá hoặc ẩn danh dữ liệu đó.',
        'Cửa hàng ngừng dùng SAPA bất cứ lúc nào và báo cho SAPA. Cửa hàng vi phạm nặng điều khoản này (như nhập dữ liệu trái phép, lạm dụng gửi tin) có thể bị SAPA tạm ngưng sau khi báo.',
      ],
    },
    {
      tieuDe: '7. Tải về và xoá dữ liệu',
      doan: [
        'Cửa hàng tự tải được danh sách khách kèm số dư ví và gói còn buổi (Cấu hình → Thông tin tiệm) và báo cáo doanh thu theo tháng, bất cứ lúc nào.',
        'Khi ngừng dùng, cửa hàng có thể yêu cầu SAPA xoá hoặc ẩn danh dữ liệu khách. Phần buộc phải giữ theo quy định pháp luật được giữ ở dạng đã gỡ danh tính.',
      ],
    },
    {
      tieuDe: '8. Bảo mật và sự cố',
      doan: [
        'SAPA dùng kết nối mã hoá, tách dữ liệu từng cửa hàng, phân quyền theo vai và sao lưu định kỳ. Không biện pháp nào tuyệt đối an toàn; SAPA cam kết xử lý nghiêm túc và cải thiện liên tục.',
        'Khi có sự cố làm lộ hoặc mất dữ liệu cá nhân liên quan đến cửa hàng, SAPA báo cho chủ cửa hàng chậm nhất 6 giờ kể từ khi SAPA phát hiện và xác nhận sự cố, và nêu rõ cách xử lý.',
      ],
    },
    {
      tieuDe: '9. Giới hạn trách nhiệm',
      doan: [
        'SAPA cố gắng để dịch vụ chạy ổn định nhưng không bảo đảm không bao giờ gián đoạn. Khi có sự cố, SAPA khôi phục sớm nhất có thể.',
        'SAPA không chịu trách nhiệm về chất lượng dịch vụ của cửa hàng, về thoả thuận giữa cửa hàng và khách, và về thiệt hại gián tiếp. Tổng mức bồi thường của SAPA cho mọi thiệt hại phát sinh từ dịch vụ không vượt quá 3 lần giá của gói dịch vụ SAPA mà cửa hàng đang dùng (giá một kỳ thanh toán theo bảng giá niêm yết).',
      ],
    },
    {
      tieuDe: '10. Thay đổi điều khoản, luật áp dụng và liên hệ',
      doan: [
        'Khi SAPA cập nhật điều khoản, chủ cửa hàng thấy dải thông báo trong ứng dụng cùng nút đọc và nút “Đồng ý”. Ai chưa đồng ý bản mới vẫn dùng được SAPA; SAPA có thể đòi đồng ý trước khi dùng thêm tính năng mới. Cửa hàng không đồng ý bản mới có thể ngừng dùng SAPA theo mục 6.',
        'Điều khoản này tuân theo pháp luật Việt Nam.',
        `${LIEN_HE.congTy} — MST ${LIEN_HE.maSoThue}.`,
        CACH_LIEN_HE,
      ],
    },
  ],
}

export interface TuyChonTrangPhapLy {
  /** Ngày hiện ở đầu trang; mặc định `NGAY_CAP_NHAT_PHAP_LY`. */
  readonly capNhat?: string
  /** Bỏ thanh chuyển giữa hai văn bản công khai (trang này không nằm trong bộ đó). */
  readonly khongTab?: boolean
  /** Hiện dải "BẢN NHÁP — chưa luật sư rà". */
  readonly banNhap?: boolean
}

/** Đường dẫn công khai (tương đối với gốc web) của từng văn bản. */
export function duongDanPhapLy(ma: VanBanPhapLy['ma']): string {
  return `/phap-ly/${ma}.html`
}

const THOAT: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}
function thoat(s: string): string {
  return s.replace(/[&<>"']/g, (c) => THOAT[c] ?? c)
}

/**
 * Trang HTML tĩnh của một văn bản. Tự chứa (CSS nội tuyến), không script, không tài nguyên ngoài.
 *
 * Bố cục theo khổ màn hình:
 *   - điện thoại: một cột, mục lục gập lại ở đầu bài;
 *   - từ 960px (máy tính, máy tính bảng ngang): khung căn giữa, mục lục dính bên trái, bài trong thẻ trắng;
 *   - hỗ trợ chế độ tối và in ra giấy.
 * Màu chủ đạo trùng `headerColor` của Mini App (#1f7a8c).
 */
export function dungTrangHtml(vb: VanBanPhapLy, tuyChon: TuyChonTrangPhapLy = {}): string {
  const muc = vb.muc.map((m, i) => ({ ...m, id: `muc-${i + 1}` }))
  const mucLuc = muc.map((m) => `<li><a href="#${m.id}">${thoat(m.tieuDe)}</a></li>`).join('')
  const noiDung = muc
    .map((m) => {
      const doan = (m.doan ?? []).map((d) => `<p>${thoat(d)}</p>`).join('')
      const y =
        m.y === undefined ? '' : `<ul>${m.y.map((x) => `<li>${thoat(x)}</li>`).join('')}</ul>`
      return `<section id="${m.id}"><h2>${thoat(m.tieuDe)}</h2>${doan}${y}</section>`
    })
    .join('\n        ')
  const tab = (tuyChon.khongTab === true ? [] : VAN_BAN_PHAP_LY)
    .map(
      (v) =>
        `<a href="${v.ma}.html"${v.ma === vb.ma ? ' aria-current="page" class="dang-xem"' : ''}>${thoat(v.tieuDe)}</a>`,
    )
    .join('')
  const banNhap =
    tuyChon.banNhap === true
      ? '<p class="cap-nhat" style="background:#fde8e8;color:#8a1c1c">BẢN NHÁP — chưa luật sư rà, chưa có hiệu lực</p>'
      : ''
  const dienThoai = LIEN_HE.dienThoai
    .map((d) => {
      const so = d.so.replace(/\s/g, '')
      return `<a href="tel:${so}">${thoat(d.so)}</a>${d.ghiChu === '' ? '' : ` <span>(${thoat(d.ghiChu)})</span>`}`
    })
    .join('<br>')
  return `<!doctype html>
<html lang="vi">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light dark">
<meta name="robots" content="index, follow">
<title>${thoat(vb.tieuDe)} — SAPA</title>
<style>
  :root { --mau: #1f7a8c; --mau-dam: #17606f; --chu: #17262d; --phu: #566a73; --nen: #eef3f4; --the: #ffffff; --vien: #dbe4e7; --nhan: #e4f1f4; }
  @media (prefers-color-scheme: dark) {
    :root { --mau: #5cc0d4; --mau-dam: #2a8fa3; --chu: #e6eef1; --phu: #9db1b9; --nen: #0f1a1e; --the: #16252b; --vien: #27393f; --nhan: #1b3038; }
  }
  * { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body { margin: 0; background: var(--nen); color: var(--chu); font: 17px/1.7 system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif; -webkit-text-size-adjust: 100%; }
  .khung { width: 100%; max-width: 1080px; margin: 0 auto; padding: 0 20px; }
  a { color: var(--mau-dam); }
  @media (prefers-color-scheme: dark) { a { color: var(--mau); } }

  /* Đầu trang */
  .dau { background: linear-gradient(135deg, #17606f, #1f7a8c); color: #fff; }
  .dau .khung { padding-top: 28px; padding-bottom: 0; }
  .thuong-hieu { font-size: 15px; letter-spacing: .6px; text-transform: uppercase; opacity: .85; margin: 0; }
  .dau h1 { margin: 6px 0 0; font-size: clamp(26px, 5vw, 38px); line-height: 1.25; }
  .tab { display: flex; gap: 4px; margin-top: 22px; }
  .tab a { flex: 1 1 0; padding: 12px 8px; border-radius: 12px 12px 0 0; color: #fff; text-align: center; text-decoration: none; font-size: 15px; line-height: 1.35; opacity: .8; }
  .tab a:hover { opacity: 1; background: rgba(255, 255, 255, .12); }
  .tab a.dang-xem { background: var(--nen); color: var(--mau-dam); font-weight: 600; opacity: 1; }
  @media (prefers-color-scheme: dark) { .tab a.dang-xem { color: var(--mau); } }

  /* Thân trang */
  .than { display: block; padding-top: 24px; padding-bottom: 56px; }
  .muc-luc-di-dong { margin-bottom: 16px; background: var(--the); border: 1px solid var(--vien); border-radius: 14px; }
  .muc-luc-di-dong summary { padding: 14px 16px; font-weight: 600; cursor: pointer; min-height: 48px; }
  .muc-luc-di-dong ol { margin: 0; padding: 0 16px 12px 40px; }
  .muc-luc-di-dong li { margin: 2px 0; }
  .muc-luc-di-dong a { display: block; padding: 8px 0; text-decoration: none; }
  .muc-luc-may-tinh { display: none; }
  .bai { background: var(--the); border: 1px solid var(--vien); border-radius: 16px; padding: 24px 20px 28px; }
  .cap-nhat { display: inline-block; margin: 0 0 12px; padding: 4px 12px; border-radius: 999px; background: var(--nhan); color: var(--mau-dam); font-size: 14px; }
  @media (prefers-color-scheme: dark) { .cap-nhat { color: var(--mau); } }
  .gioi-thieu { margin: 0 0 8px; font-size: 18px; color: var(--phu); }
  section { scroll-margin-top: 16px; }
  h2 { margin: 32px 0 8px; font-size: 20px; line-height: 1.35; color: var(--mau-dam); }
  @media (prefers-color-scheme: dark) { h2 { color: var(--mau); } }
  p { margin: 8px 0; }
  ul { margin: 8px 0; padding-left: 22px; }
  li { margin: 6px 0; }

  /* Thẻ hỗ trợ + chân trang */
  .ho-tro { margin-top: 36px; padding: 20px; border-radius: 14px; background: var(--nhan); }
  .ho-tro h3 { margin: 0 0 6px; font-size: 17px; }
  .ho-tro p { margin: 4px 0; }
  .chan { margin-top: 20px; color: var(--phu); font-size: 14px; text-align: center; }

  @media (min-width: 960px) {
    body { font-size: 18px; }
    .than { display: grid; grid-template-columns: 270px minmax(0, 1fr); gap: 40px; align-items: start; padding-top: 32px; }
    .muc-luc-di-dong { display: none; }
    .muc-luc-may-tinh { display: block; position: sticky; top: 24px; max-height: calc(100vh - 48px); overflow-y: auto; }
    .muc-luc-may-tinh h2 { margin: 0 0 10px; font-size: 14px; letter-spacing: .6px; text-transform: uppercase; color: var(--phu); }
    .muc-luc-may-tinh ol { margin: 0; padding: 0; list-style: none; border-left: 2px solid var(--vien); }
    .muc-luc-may-tinh a { display: block; margin-left: -2px; padding: 7px 12px; border-left: 2px solid transparent; font-size: 15px; line-height: 1.4; color: var(--phu); text-decoration: none; }
    .muc-luc-may-tinh a:hover { color: var(--mau-dam); border-left-color: var(--mau); background: var(--nhan); }
    .tab a { flex: none; padding: 12px 18px; font-size: 16px; }
    .bai { padding: 40px 48px 44px; }
    h2 { font-size: 22px; }
  }
  @media print {
    body { background: #fff; color: #000; font-size: 12pt; }
    .dau, .tab, .muc-luc-di-dong, .muc-luc-may-tinh, .chan { display: none; }
    .khung { max-width: none; padding: 0; }
    .than { display: block; padding: 0; }
    .bai { border: 0; padding: 0; }
    .ho-tro { background: none; border: 1px solid #999; }
  }
</style>
</head>
<body>
<header class="dau">
  <div class="khung">
    <p class="thuong-hieu">SAPA · Đặt lịch hẹn</p>
    <h1>${thoat(vb.tieuDe)}</h1>
    ${tab === '' ? '' : `<nav class="tab" aria-label="Văn bản pháp lý">${tab}</nav>`}
  </div>
</header>
<div class="khung than">
  <details class="muc-luc-di-dong"><summary>Mục lục</summary><ol>${mucLuc}</ol></details>
  <nav class="muc-luc-may-tinh" aria-label="Mục lục"><h2>Mục lục</h2><ol>${mucLuc}</ol></nav>
  <main class="bai">
    ${banNhap}<p class="cap-nhat">Cập nhật ngày ${thoat(tuyChon.capNhat ?? NGAY_CAP_NHAT_PHAP_LY)}</p>
    <p class="gioi-thieu">${thoat(vb.gioiThieu)}</p>
        ${noiDung}
    <aside class="ho-tro">
      <h3>Cần hỗ trợ?</h3>
      <p>${thoat(LIEN_HE.congTy)} — MST ${LIEN_HE.maSoThue}</p>
      <p>${dienThoai}</p>
      <p><a href="mailto:${LIEN_HE.email}">${LIEN_HE.email}</a></p>
    </aside>
    <p class="chan">© ${thoat(LIEN_HE.congTy)}</p>
  </main>
</div>
</body>
</html>
`
}

/**
 * Bản nháp dạng Markdown để chủ dự án / luật sư đọc và góp ý (`docs/phap-ly/dieu-khoan-cua-hang-BAN-NHAP.md`).
 * SINH TỪ cùng nguồn với trang web (`pnpm phap-ly:tao`) — sửa nội dung ở file này, không sửa tệp Markdown.
 * Cuối tệp liệt kê mọi chỗ `[CẦN QUYẾT: …]` để người rà không phải tìm.
 */
export function dungMarkdownNhap(vb: VanBanPhapLy, phienBan: string, banNhap: boolean): string {
  const dong: string[] = [`# ${vb.tieuDe}`, '']
  if (banNhap) {
    dong.push(
      '> **BẢN NHÁP — chưa luật sư rà. Chưa có hiệu lực.** Không đưa lên trang công khai hay trong ứng dụng cho tới khi chủ dự án duyệt văn bản.',
      '',
    )
  }
  dong.push(
    `Phiên bản: \`${phienBan}\`. Tệp này được sinh tự động từ \`packages/shared/src/phap-ly.ts\` bằng \`pnpm phap-ly:tao\` — muốn sửa chữ thì sửa ở file đó rồi chạy lại lệnh.`,
    '',
    vb.gioiThieu,
    '',
  )
  for (const m of vb.muc) dong.push(...mucMarkdown(m))
  const canQuyet = vb.muc.flatMap((m) => choCanQuyet(m).map((c) => `${m.tieuDe} — ${c}`))
  if (canQuyet.length > 0) {
    dong.push('## Việc cần chủ dự án / luật sư quyết', '')
    for (const c of canQuyet) dong.push(`- ${c}`)
    dong.push('')
  }
  return dong.join('\n')
}

function mucMarkdown(m: MucPhapLy): string[] {
  const dong = [`## ${m.tieuDe}`, '']
  for (const d of m.doan ?? []) dong.push(d, '')
  const y = m.y ?? []
  for (const x of y) dong.push(`- ${x}`)
  if (y.length > 0) dong.push('')
  return dong
}

/** Mọi chỗ `[CẦN QUYẾT: …]` trong một mục (đoạn + gạch đầu dòng). */
function choCanQuyet(m: MucPhapLy): string[] {
  const ra: string[] = []
  for (const t of [...(m.doan ?? []), ...(m.y ?? [])]) {
    for (const k of t.matchAll(/\[CẦN QUYẾT: ([^\]]+)\]/g)) {
      if (k[1] !== undefined) ra.push(k[1])
    }
  }
  return ra
}
